// React
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

// Utils
import cn from '@/utils/cn';

// Components
import Picto from '@/components/atoms/Picto/Picto';

// Types
import { FooterMenuType } from '@/components/organisms/Footer/FooterType';

// Styles
import styles from './BreadCrumb.module.css';

interface BreadCrumbProps {
  items: FooterMenuType[];
  className?: string;
  theme?: 'transparent' | 'light';
  noPadding?: boolean;
}

function BreadCrumb({ items, className, theme = 'light', noPadding = false }: BreadCrumbProps) {
  // Générer le JSON-LD pour Schema.org
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: item.columns,
      name: item.label,
      ...(index < items.length - 1 && { item: item.links }),
    })),
  };

  useEffect(() => {
    // Ajouter le script JSON-LD au head
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'breadcrumb-schema';
    script.textContent = JSON.stringify(breadcrumbSchema);

    // Supprimer l'ancien script s'il existe
    const existingScript = document.getElementById('breadcrumb-schema');
    if (existingScript) {
      existingScript.remove();
    }

    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('breadcrumb-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [breadcrumbSchema]);

  return (
    <nav
      aria-label="Fil d'Ariane"
      className={cn([styles.section, styles[theme], noPadding && styles.noPadding, className, 'max-width-wrapper'])}
    >
      <ol className={cn([styles.list])}>
        {items.map((listItem, index) => (
          <li key={listItem.label} className={styles.item}>
            {index === items.length - 1 ? (
              <span aria-current="page">{listItem.label}</span>
            ) : (
              <Link
                className={styles.link}
                to={listItem.links && listItem.links[0].link ? listItem.links[0].link : ''}
                aria-label={`Aller à ${listItem.label}`}
              >
                <span>{listItem.label}</span>
              </Link>
            )}

            {index < items.length - 1 && <Picto icon="chevronDown" className={styles.icon} aria-hidden="true" />}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default BreadCrumb;
